import { useTranslation } from "react-i18next";
import { BidV2Status, type EvalBidDto } from "../../../../autogen/bff-api";
import type { FocusProgress, FocusSubject } from "../../../../common/focus/focusSubject";
import type { StatusVariant } from "../../../../common/components/StatusVariant";
import type { TenderQualificationRow } from "../qualification/tender/deriveTenderQualification";
import type { MinimumRequirementRow } from "../minimum-requirements/deriveMinimumRequirementReview";

interface BidReviewRow {
  bidId: string;
  supplierName: string;
  reviewed: number;
  total: number;
}

interface Described {
  status: StatusVariant;
  detail: string;
  isDecided: boolean;
  isSetAside?: boolean;
  countsTowardProgress?: boolean;
}

const buildSubjects = <T extends BidReviewRow>(
  rows: T[],
  describe: (row: T) => Described,
  progressLabel: (decided: number, total: number) => string,
): { subjects: FocusSubject[]; progress: FocusProgress } => {
  const described = rows.map((row) => ({ row, ...describe(row) }));
  const counted = described.filter((entry) => entry.countsTowardProgress !== false);
  const decided = counted.filter((entry) => entry.isDecided).length;
  return {
    subjects: described.map(({ row, status, detail, isSetAside }) => ({
      id: row.bidId,
      title: row.supplierName,
      status,
      detail,
      isSetAside,
    })),
    progress: {
      value: counted.length === 0 ? 0 : Math.round((decided / counted.length) * 100),
      label: progressLabel(decided, counted.length),
    },
  };
};

export const useTenderQualificationSubjects = (rows: TenderQualificationRow[]) => {
  const { t } = useTranslation();
  const detail = (row: TenderQualificationRow, state: string) =>
    String(
      t("{{reviewed}}/{{total}} requirements · {{state}}", {
        reviewed: row.reviewed,
        total: row.total,
        state,
      }),
    );
  return buildSubjects(
    rows,
    (row) => {
      if (row.state === "QUALIFIED")
        return { status: "ok", detail: detail(row, String(t("Qualified"))), isDecided: true };
      if (row.state === "REJECTED")
        return { status: "issue", detail: String(t("rejected · not assessed")), isDecided: true, isSetAside: true };
      return { status: "neutral", detail: detail(row, String(t("Under review"))), isDecided: false };
    },
    (decided, total) => String(t("{{decided}} of {{total}} decided", { decided, total })),
  );
};

export const useMinimumRequirementSubjects = (rows: MinimumRequirementRow[]) => {
  const { t } = useTranslation();
  const detail = (row: MinimumRequirementRow) =>
    String(
      t("{{reviewed}}/{{total}} minimum requirements", { reviewed: row.reviewed, total: row.total }),
    );
  return buildSubjects(
    rows,
    (row) => {
      if (row.state === "REJECTED")
        return {
          status: "issue",
          detail: String(t("rejected · not assessed")),
          isDecided: false,
          isSetAside: true,
          countsTowardProgress: false,
        };
      const assessed = row.total > 0 && row.reviewed === row.total;
      return { status: assessed ? "ok" : "neutral", detail: detail(row), isDecided: assessed };
    },
    (assessed, total) => String(t("{{assessed}} of {{total}} assessed", { assessed, total })),
  );
};

interface EvaluationRow extends BidReviewRow {
  isRejected: boolean;
  weightedTotal: number;
}

// BFF: an auto-computed cell is a price row, scored from the tendered price and never by hand.
export const evaluationRowOf = (bid: EvalBidDto): EvaluationRow => {
  const scorable = bid.cells.filter((cell) => !cell.isAutoComputed);
  return {
    bidId: bid.bidId,
    supplierName: bid.supplierName,
    reviewed: scorable.filter((cell) => cell.points != null).length,
    total: scorable.length,
    isRejected: bid.status === BidV2Status.Rejected,
    weightedTotal: bid.weightedTotal,
  };
};

export const useEvaluationSubjects = (bids: EvalBidDto[]) => {
  const { t } = useTranslation();
  return buildSubjects(
    bids.map(evaluationRowOf),
    (row) => {
      if (row.isRejected)
        return {
          status: "issue",
          detail: String(t("rejected · not assessed")),
          isDecided: false,
          isSetAside: true,
          countsTowardProgress: false,
        };
      const scored = row.total > 0 && row.reviewed === row.total;
      return {
        status: scored ? "ok" : "neutral",
        detail: String(
          scored
            ? t("{{scored}}/{{total}} criteria · {{points}} points", {
              scored: row.reviewed,
              total: row.total,
              points: row.weightedTotal.toFixed(1),
            })
            : t("{{scored}}/{{total}} criteria", { scored: row.reviewed, total: row.total }),
        ),
        isDecided: scored,
      };
    },
    (assessed, total) => String(t("{{assessed}} of {{total}} assessed", { assessed, total })),
  );
};
